import { useState, useCallback } from 'react'
import { calcLine } from './CorrectionProductTable'

const toYMD = (d = new Date()) => d.toISOString().slice(0, 10)
const addDays = (d, n) => {
	const dt = new Date(d)
	dt.setDate(dt.getDate() + n)
	return toYMD(dt)
}

const recalcDiff = (before, after) => {
	const b = calcLine(before?.price, before?.count, before?.taxPercent)
	const a = calcLine(after?.price, after?.count, after?.taxPercent)
	return {
		before: b,
		after: a,
		price: +(a.price - b.price).toFixed(2),
		count: +(a.count - b.count).toFixed(4),
		taxPercent: a.taxPercent,
		subtotal: +(a.subtotal - b.subtotal).toFixed(2),
		taxValue: +(a.taxValue - b.taxValue).toFixed(2),
		total: +(a.total - b.total).toFixed(2),
	}
}

const recalcTotals = (products) => {
	const subtotal = +products.reduce((s, p) => s + (p.subtotal || 0), 0).toFixed(2)
	const taxTotal = +products.reduce((s, p) => s + (p.taxValue || 0), 0).toFixed(2)
	const total = +(subtotal + taxTotal).toFixed(2)
	return { subtotal, taxTotal, discountValue: 0, total, discountPercent: 0 }
}

export default function useKorektaFormState(initialForm) {
	const baseForm = initialForm || {
		customerId: '',
		customerName: '',
		invoiceDate: toYMD(),
		correctedInvoiceNumber: '',
		sourceInvoiceId: '',
		correctionReason: '',
		publicNotes: '',
		privateNotes: '',
		paymentType: 'transfer',
		paymentDate: '',
		products: [],
		subtotal: 0,
		taxTotal: 0,
		total: 0,
		discountValue: 0,
		discountPercent: 0,
		dueDate: addDays(new Date(), 14),
		status: 'issued',
		companyId: '',
		companyName: '',
	}

	const [form, setForm] = useState(() => {
		const products = (baseForm.products || []).map(p => {
			const diff = recalcDiff(p.before, p.after || p.before)
			return { ...p, ...diff }
		})
		return { ...baseForm, products, ...recalcTotals(products) }
	})

	const setField = useCallback((name, value) => {
		setForm(prev => ({ ...prev, [name]: value }))
	}, [])

	const setFormState = useCallback((next) => {
		setForm(prev => {
			const merged = typeof next === 'function' ? next(prev) : { ...prev, ...next }
			const products = (merged.products || []).map(p => {
				const diff = recalcDiff(p.before, p.after || p.before)
				return { ...p, ...diff }
			})
			return { ...merged, products, ...recalcTotals(products) }
		})
	}, [])

	const setAfterField = useCallback((index, field, value) => {
		setForm(prev => {
			const products = prev.products.map((row, i) => {
				if (i !== index) return row
				const after = { ...(row.after || {}), [field]: value }
				const diff = recalcDiff(row.before, after)
				return { ...row, ...diff }
			})
			return { ...prev, products, ...recalcTotals(products) }
		})
	}, [])

	return { form, setField, setForm: setFormState, setAfterField }
}
